/**
 * Centralized Logger Utility
 *
 * Environment-controlled console logging system
 * - Controlled by APP_DEBUG_JS environment variable
 * - Zero performance impact when disabled (dead code elimination)
 * - Drop-in replacement for console methods
 *
 * Usage:
 *   import logger from '@/utils/logger'
 *   logger.log('Debug message')
 *   logger.error('Error message')  // Always shown (critical)
 *   logger.warn('Warning')
 *   logger.info('Info')
 *   logger.debug('Debug details')
 *
 * Environment:
 *   APP_DEBUG_JS=true   - Enable all logs (development)
 *   APP_DEBUG_JS=false  - Disable debug logs (production)
 */

class Logger {
  constructor() {
    // Check if debugging is enabled via environment variable
    // Default to false for production safety
    this.isEnabled = process.env.APP_DEBUG_JS === 'true' || process.env.NODE_ENV === 'development'

    // Performance: Bind methods once during construction
    this._log = console.log.bind(console)
    this._warn = console.warn.bind(console)
    this._error = console.error.bind(console)
    this._info = console.info.bind(console)
    this._debug = console.debug.bind(console)
  }

  /**
   * Standard log - disabled in production
   */
  log(...args) {
    if (this.isEnabled) {
      this._log(...args)
    }
  }

  /**
   * Warning log - disabled in production
   */
  warn(...args) {
    if (this.isEnabled) {
      this._warn(...args)
    }
  }

  /**
   * Error log - ALWAYS enabled (critical errors should always be visible)
   */
  error(...args) {
    this._error(...args)
  }

  /**
   * Info log - disabled in production
   */
  info(...args) {
    if (this.isEnabled) {
      this._info(...args)
    }
  }

  /**
   * Debug log - disabled in production
   */
  debug(...args) {
    if (this.isEnabled) {
      this._debug(...args)
    }
  }

  /**
   * Group logs together (for better organization)
   */
  group(label) {
    if (this.isEnabled && console.group) {
      console.group(label)
    }
  }

  /**
   * End log group
   */
  groupEnd() {
    if (this.isEnabled && console.groupEnd) {
      console.groupEnd()
    }
  }

  /**
   * Table display (useful for arrays/objects)
   */
  table(data) {
    if (this.isEnabled && console.table) {
      console.table(data)
    }
  }

  /**
   * Time tracking start
   */
  time(label) {
    if (this.isEnabled && console.time) {
      console.time(label)
    }
  }

  /**
   * Time tracking end
   */
  timeEnd(label) {
    if (this.isEnabled && console.timeEnd) {
      console.timeEnd(label)
    }
  }

  /**
   * Check if logging is currently enabled
   */
  get enabled() {
    return this.isEnabled
  }
}

// Export singleton instance
export default new Logger()
